import { COMMA, ENTER, SEMICOLON, SPACE } from '@angular/cdk/keycodes';
import { Component, Input, OnInit, EventEmitter, Output } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { Subject } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

import { UserService } from '../../../user.service';

import { CommonApiService } from '../../services/common-api.service';

import { BukTicketIssueData } from '../../models/buk-ticket.model';

import { CP } from '../../../cp/models/cp.model';
import { multiEmailValidator } from '../../../cp/utils/validators';

const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/gi;

enum IssueType {
  Email,
  Number,
}

@Component({
  selector: 'app-buk-ticket-form',
  templateUrl: './buk-ticket-form.component.html',
  styleUrls: ['./buk-ticket-form.component.scss'],
})
export class BukTicketFormComponent implements OnInit {
  @Input() contents: { thumbnail: string; title: string; author?: string };
  @Input() hideSenderInfo: boolean; // 라이브러리, 정기간행물
  @Input() cp: CP;
  @Input() imprintName: string;
  @Input() disableIssue = false;
  @Input() gaTrackingId: string;

  @Output() issueTicket = new EventEmitter<BukTicketIssueData>();

  // 열람기간: 1주, 2주, 1개월, 3개월, 6개월, 1년
  public _periods = ['1w', '2w', '1m', '3m', '6m', '1y', 'unlimited'];
  // 유효기간: 3개월, 6개월, 1년
  public _expiryDates = ['3m', '6m', '1y'];

  public readonly _separatorKeysCodes = [ENTER, COMMA, SEMICOLON, SPACE];
  public _isEmailCollapsed = false;
  public _emails = [];
  public _emailInputChange$: Subject<any> = new Subject<any>(); // any => InputEvent

  public _formGroup: FormGroup;

  public _userEmail: string;

  public _issueType: IssueType = IssueType.Email;

  constructor(
    private formBuilder: FormBuilder,
    private appCommonAPIService: CommonApiService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this._emailInputChange$
      .pipe(
        debounceTime(300),
        filter(() => this._emails.length === 0)
      )
      .subscribe((inputEvent) => {
        this._setEmailControlValue(inputEvent.srcElement.value);
      });

    this._formGroup = this.formBuilder.group({
      method: null,
      period: ['1w', Validators.required],
      expiryDate: [null],
      //
      sender: ['cp', Validators.required],
      language: [
        this.userService.getCurrentUser().language === 'ko' ? 'ko' : 'en',
        Validators.required,
      ],
      email: ['', [Validators.required, multiEmailValidator]],
      message: [''],
      //
      count: [
        null,
        [Validators.required, Validators.min(1), Validators.max(100000)],
      ],
    });

    this._userEmail = this.userService.getCurrentUser().email;

    this._onIssueTypeChange(); // trigger manually
  }

  get value(): BukTicketIssueData {
    return this._formGroup.value;
  }

  get invalid(): boolean {
    return this._formGroup.invalid;
  }

  clearReceiver(): void {
    // clear email
    this._emails = [];
    this._formGroup.controls.email.reset();

    // clear message
    this._formGroup.controls.message.reset();
  }

  _collapseEmailChipsIfNeeded(): void {
    this._isEmailCollapsed = this._emails.length >= 10;
  }

  _addEmail(value: string): void {
    value = (value || '').trim();

    if (value && EMAIL_REGEX.test(value)) {
      this._emails.push(value);
      this._setEmailControlValue(this._emails.join(','));
    }
  }

  _setEmailControlValue(value: string): void {
    this._formGroup.controls.email.setValue(value);
    this._formGroup.controls.email.markAsDirty();
  }

  _onEmailChipRemoved(email: string): void {
    const index = this._emails.indexOf(email);

    if (index >= 0) {
      this._emails.splice(index, 1);
      this._formGroup.controls.email.setValue(this._emails.join(','));
      this._formGroup.controls.email.markAsDirty();
    }
  }

  _onEmailChipClick(): void {
    this._isEmailCollapsed = false;
  }

  _onEmailInputFocus(): void {
    this._isEmailCollapsed = false;
  }

  _onEmailInputBlur(): void {
    this._collapseEmailChipsIfNeeded();
  }

  _onEmailChipInputTokenEnd(event: MatChipInputEvent): void {
    const input = event.input;

    this._addEmail(event.value);

    if (input) {
      input.value = '';
    }
  }

  _onEmailPaste(event: ClipboardEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const text = (event.clipboardData || (window as any).clipboardData).getData(
      'text'
    );

    const matches = text.matchAll(EMAIL_REGEX);
    for (const m of matches) {
      this._addEmail(m[1]);
    }
  }

  _onFileChange(files: any): void {
    this.appCommonAPIService
      .extractEmailFromFile(files)
      .subscribe((extractedEmails) => {
        extractedEmails.forEach((email) => this._addEmail(email));
        this._collapseEmailChipsIfNeeded();
      });
  }

  _onIssueTypeChange(): void {
    const controlsForEmailIssue = ['language', 'email', 'message', 'sender'];
    const controlsForNumberIssue = ['count'];

    let method;
    let willBeEnabled;
    let willBeDisabled;

    if (this._issueType === IssueType.Email) {
      method = 'email';

      willBeEnabled = controlsForEmailIssue;
      willBeDisabled = controlsForNumberIssue;
    } else {
      method = 'download';

      willBeEnabled = controlsForNumberIssue;
      willBeDisabled = controlsForEmailIssue;
    }

    this._formGroup.get('method').setValue(method, { emitEvent: false });

    willBeEnabled.forEach((controlName) => {
      this._formGroup.get(controlName).enable({ emitEvent: false });
    });

    willBeDisabled.forEach((controlName) => {
      this._formGroup.get(controlName).disable({ emitEvent: false });
    });

    this._formGroup.updateValueAndValidity();
  }

  onIssueClick(): void {
    this.issueTicket.emit(this.value);
  }
}
